// Mixpanel product-analytics adapter.
//
// A thin, token-gated singleton over mixpanel-browser. It runs ALONGSIDE the
// in-house journey beacon (beacon.ts) rather than replacing it: the journey
// beacon stays the source of truth for the Django funnel + triggers, and every
// journey event is mirrored here so the same stream lands in Mixpanel for
// product analytics (funnels, retention, segmentation).
//
// Design rules:
//   - No-op on the server and when no token is configured, so importing this
//     module can never break a render or a build.
//   - init() is idempotent — safe to call from multiple entry points (the
//     authed portal mounts it via AnalyticsProvider; the unauth interview room
//     calls it lazily from the post-interview screen).
//   - Identity uses the Firebase uid (stable DB-backed id), never email.
//     Simplified ID Merge (Mixpanel account default since 2024) merges the
//     pre-login anonymous $device_id into the uid on the first identify().
//   - We do NOT auto-track pageviews. Instrumentation is opt-in per event,
//     matching the existing beacon philosophy.

import mixpanel from "mixpanel-browser";
import {
  hasAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";

// Mixpanel project token. A project token is a PUBLIC client identifier (it
// ships in the browser bundle, like the Firebase apiKey/measurementId in
// components/context/firebase.js), so a hardcoded fallback is safe and keeps
// analytics working in any environment whose .env omits the var.
const MIXPANEL_TOKEN =
  process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || "5c453a9ce3fa6819879497aa833dcf43";

type EventProps = Record<string, unknown>;

let ready = false;
let initStarted = false;

const isBrowser = (): boolean => typeof window !== "undefined";

const activateMixpanel = (): void => {
  // Consent is already explicit. Suppress Mixpanel's automatic $opt_in event,
  // resume its senders, and restore properties cleared by a prior withdrawal.
  mixpanel.opt_in_tracking({ track: () => {} });
  (mixpanel as typeof mixpanel & { start_batch_senders?: () => void })
    .start_batch_senders?.();
  mixpanel.register({ platform: "web" });
};

/** Initialise Mixpanel once. Idempotent and safe to call on every render and
 *  from any entry point. No-op on the server or without a token. */
export const initMixpanel = (): void => {
  if (!isBrowser() || !hasAnalyticsConsent() || initStarted || !MIXPANEL_TOKEN) return;
  initStarted = true;
  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      persistence: "localStorage",
      track_pageview: false,
      debug: process.env.NODE_ENV !== "production",
      opt_out_tracking_by_default: true,
    });
    activateMixpanel();
    ready = true;
  } catch {
    // Never let analytics setup throw into the app.
    ready = false;
    initStarted = false;
  }
};

/** Tie subsequent events to a known user. Pass the Firebase uid. */
export const mpIdentify = (uid?: string | null): void => {
  if (!ready || !hasAnalyticsConsent() || !uid) return;
  try {
    mixpanel.identify(uid);
  } catch {
    /* swallow */
  }
};

/** Clear identity on logout so the next user's session does not merge. */
export const mpReset = (): void => {
  if (!ready || !hasAnalyticsConsent()) return;
  try {
    mixpanel.reset();
  } catch {
    /* swallow */
  }
};

/** Track one event. Lazily initialises so call sites outside AnalyticsProvider
 *  (e.g. the interview room) still land data. Omit null/empty props upstream. */
export const mpTrack = (name: string, props?: EventProps): void => {
  if (!isBrowser() || !hasAnalyticsConsent()) return;
  if (!ready) initMixpanel();
  if (!ready) return;
  try {
    mixpanel.track(name, props);
  } catch {
    /* swallow */
  }
};

/** Register super properties attached to every subsequent event. */
export const mpRegister = (props: EventProps): void => {
  if (!ready || !hasAnalyticsConsent()) return;
  try {
    mixpanel.register(props);
  } catch {
    /* swallow */
  }
};

/** Set User Profile attributes (current state). Only for identified users. */
export const mpPeopleSet = (props: EventProps): void => {
  if (!ready || !hasAnalyticsConsent()) return;
  try {
    mixpanel.people.set(props);
  } catch {
    /* swallow */
  }
};

if (isBrowser()) {
  subscribeToAnalyticsConsent((consent) => {
    if (consent === "accepted") {
      if (!ready) initMixpanel();
      if (ready) {
        try {
          activateMixpanel();
        } catch {
          /* swallow */
        }
      }
      return;
    }

    if (ready) {
      try {
        mixpanel.opt_out_tracking({
          clear_persistence: true,
          delete_user: false,
        } as Parameters<typeof mixpanel.opt_out_tracking>[0] & {
          clear_persistence: boolean;
        });
        (mixpanel as typeof mixpanel & { stop_batch_senders?: () => void })
          .stop_batch_senders?.();
      } catch {
        /* swallow */
      }
    }
  });
}
